import { formatCountdown } from '../../utils/formatting';

const TAB_DEFS = [
  { key: 'performance', label: 'Performance', shortLabel: 'Performance', always: true },
  { key: 'research', label: 'Research', shortLabel: 'Research', always: false },
  { key: 'recommendations', label: 'Setups', shortLabel: 'Setups', always: false },
  { key: 'trades', label: 'Trades', shortLabel: 'Trades', always: true },
  { key: 'setup', label: 'Config', shortLabel: 'Config', always: true },
];

/**
 * URL-driven tab strip for the workspace.
 * Per wireframe Direction B: tabs + refresh timer + chat placeholder on right.
 *
 * Desktop (md+): underline-style tabs.
 * Mobile (<md): horizontally scrollable pill buttons.
 */
export function WsTabs({
  tab,
  setTab,
  hasAgent,
  timeWindow,
  onTimeWindowChange,
  onRefresh,
  loading,
  pollInterval = 30000,
  nextRefreshIn = 0,
}) {
  const visibleTabs = TAB_DEFS.filter(t => t.always || hasAgent);

  return (
    <div className="border-b border-slate-700">
      {/* Desktop tab strip */}
      <div className="hidden md:flex items-center px-4">
        {visibleTabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm transition-colors whitespace-nowrap ${
              tab === t.key
                ? 'text-white font-bold border-b-2 border-cyan-400'
                : 'text-slate-400 hover:text-slate-200 border-b-2 border-transparent'
            }`}
          >
            {t.label}
          </button>
        ))}

        {/* Right-side controls */}
        <div className="ml-auto flex items-center gap-2 text-xs text-slate-400 py-2">
          {/* Time window selector (performance tab only) */}
          {tab === 'performance' && (
            <select
              value={timeWindow}
              onChange={(e) => onTimeWindowChange(e.target.value)}
              className="appearance-none bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-200 hover:border-zinc-600 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="24H">24H</option>
              <option value="7D">7D</option>
              <option value="30D">30D</option>
            </select>
          )}

          {/* Refresh button */}
          <button
            onClick={onRefresh}
            disabled={loading}
            className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-slate-200 transition-colors disabled:opacity-50"
            title="Refresh portfolio"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          {pollInterval > 0 && (
            <span className="hidden lg:inline">{formatCountdown(nextRefreshIn)}</span>
          )}

          {/* Chat placeholder (Phase 5) */}
          <button disabled className="opacity-30 cursor-not-allowed text-sm text-slate-500 flex items-center gap-1 ml-2">
            💬 <span className="hidden lg:inline">ask agent</span>
          </button>
        </div>
      </div>

      {/* Mobile pill tabs */}
      <div className="md:hidden flex items-center gap-1.5 px-2 py-2 overflow-x-auto scrollbar-none">
        {visibleTabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 text-xs rounded-full whitespace-nowrap transition-colors flex-shrink-0 ${
              tab === t.key
                ? 'bg-cyan-600 text-white font-semibold'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            {t.shortLabel}
          </button>
        ))}
      </div>
    </div>
  );
}
